import type { FC } from 'react';
import * as React from 'react';
import { useCallback } from 'react';
import type { IMangopayConnectFormValues } from './MangopayConnectForm';
import { useTranslation } from 'react-i18next';
import { Grid, Stack, Typography } from '@mui/material';
import {
  CountrySelector,
  FDatePicker,
  FTextInput,
} from '@pflegenavi/web-components';
import { type FormikProps, getIn } from 'formik';

interface MangopayConnectFormStep2Props {
  formik: FormikProps<IMangopayConnectFormValues>;
  countryOptions: Array<{
    label: string;
    value: string;
  }>;
  disabled: boolean;
}

export const MangopayConnectFormStep2: FC<MangopayConnectFormStep2Props> = ({
  formik,
  countryOptions,
  disabled,
}) => {
  const { t } = useTranslation();

  const onBlurNationality = useCallback(() => {
    formik.setFieldTouched('legal_representative.nationality_id', true);
  }, [formik]);

  const onChangeNationality = useCallback(
    (newNationalityId: string) => {
      if (
        formik.values.legal_representative.nationality_id === newNationalityId
      ) {
        return;
      }
      formik.setFieldValue(
        'legal_representative.nationality_id',
        newNationalityId
      );
      formik.setFieldError('legal_representative.nationality_id', undefined);
    },
    [formik]
  );

  const onBlurResidence = useCallback(() => {
    formik.setFieldTouched(
      'legal_representative.country_of_residence_id',
      true
    );
  }, [formik]);

  const onChangeResidence = useCallback(
    (newCountryId: string) => {
      if (
        formik.values.legal_representative.country_of_residence_id ===
        newCountryId
      ) {
        return;
      }
      formik.setFieldValue(
        'legal_representative.country_of_residence_id',
        newCountryId
      );
      formik.setFieldError(
        'legal_representative.country_of_residence_id',
        undefined
      );
    },
    [formik]
  );

  return (
    <Stack gap={2}>
      <Typography color="grey.500">
        {t('service-provider-app.connect-account.legal-representative-details')}
      </Typography>
      <Grid container spacing={2}>
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <FTextInput
            id="legal_representative.first_name"
            name="legal_representative.first_name"
            label={t('service-provider-app.connect-account.form.first-name')}
            formik={formik}
            disabled={disabled}
            fullWidth
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <FTextInput
            id="legal_representative.last_name"
            name="legal_representative.last_name"
            label={t('service-provider-app.connect-account.form.last-name')}
            formik={formik}
            disabled={disabled}
            fullWidth
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <FDatePicker
            id="legal_representative.birth_date"
            name="legal_representative.birth_date"
            label={t('service-provider-app.connect-account.form.birthdate')}
            formik={formik}
            maxDate={new Date()}
            disabled={disabled}
            openTo={
              formik.values.legal_representative.birth_date ? 'day' : 'year'
            }
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <CountrySelector
            label={t('service-provider-app.connect-account.form.nationality')}
            onChangeCountry={onChangeNationality}
            onBlur={onBlurNationality}
            countries={countryOptions}
            countryId={formik.values.legal_representative.nationality_id}
            disabled={disabled}
            name={'legal_representative.nationality_id'}
            errorMessage={
              getIn(formik.touched, 'legal_representative.nationality_id') &&
              getIn(formik.errors, 'legal_representative.nationality_id')
            }
            fullWidth
          />
        </Grid>
        <Grid size={12}>
          <CountrySelector
            onChangeCountry={onChangeResidence}
            label={t(
              'service-provider-app.connect-account.form.country-of-residence'
            )}
            onBlur={onBlurResidence}
            countries={countryOptions}
            countryId={
              formik.values.legal_representative.country_of_residence_id
            }
            disabled={disabled}
            name={'legal_representative.country_of_residence_id'}
            errorMessage={
              getIn(
                formik.touched,
                'legal_representative.country_of_residence_id'
              ) &&
              getIn(
                formik.errors,
                'legal_representative.country_of_residence_id'
              )
            }
            fullWidth
          />
        </Grid>
      </Grid>
    </Stack>
  );
};
